import React, { useEffect, useState } from 'react';
import {
  createExtraNode, fetchExtras, patchExtraAssignments,
} from '../../lib/shiftApi';
import { useShift } from '../../context/ShiftContext';

function assignLine(a) {
  return a.date;
}

function LockedBar({ children }) {
  return (
    <div className="shift-extra-locked">
      <span className="shift-extra-locked-body">{children}</span>
      <span className="shift-extra-locked-tag">已固定</span>
    </div>
  );
}

export default function ShiftExtraCreate({ onCreated }) {
  const { bizDate, bumpTreeTick } = useShift();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [nodeType, setNodeType] = useState('check');
  const [parentId, setParentId] = useState('');
  const [categories, setCategories] = useState([]);
  const [newCatTitle, setNewCatTitle] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState(bizDate || '');
  const [extras, setExtras] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const selected = extras.find((x) => x.id === selectedId) || null;
  const locked = !!selectedId;

  const loadExtras = async () => {
    try {
      const r = await fetchExtras();
      if (r?.ok) {
        setExtras(r.extras || []);
        setCategories(r.categories || []);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (bizDate && !date) setDate(bizDate);
    if (bizDate && !dates.length) setDates([bizDate]);
  }, [bizDate, date, dates.length]);

  useEffect(() => {
    if (!open) return;
    loadExtras();
  }, [open]);

  const pickExisting = (id) => {
    const n = Number(id);
    if (!n) {
      setSelectedId(null);
      return;
    }
    const hit = extras.find((x) => x.id === n);
    if (!hit) return;
    setSelectedId(hit.id);
    setTitle(hit.title);
    setNodeType(hit.nodeType === 'text' ? 'text' : 'check');
    setParentId(hit.parentId ? String(hit.parentId) : '');
    setHistOpen(false);
  };

  const onTitleChange = (v) => {
    setTitle(v);
    const hit = extras.find((x) => x.title === v);
    if (hit) {
      setSelectedId(hit.id);
      setNodeType(hit.nodeType === 'text' ? 'text' : 'check');
      setParentId(hit.parentId ? String(hit.parentId) : '');
    } else {
      setSelectedId(null);
    }
  };

  const reset = () => {
    setTitle('');
    setNodeType('check');
    setParentId('');
    setNewCatTitle('');
    setShowNewCat(false);
    setDate(bizDate || '');
    setDates(bizDate ? [bizDate] : []);
    setSelectedId(null);
    setHistOpen(false);
    setErr('');
  };

  const submit = async () => {
    const t = title.trim();
    if (!t) { setErr('請填標題'); return; }
    const list = dates.length ? dates : (date ? [date] : []);
    if (!list.length || list.some((d) => !/^\d{4}-\d{2}-\d{2}$/.test(d))) { setErr('請選日期'); return; }
    setSaving(true); setErr('');
    const payload = {
      extraKind: 'day',
      treeScope: 'extra',
      parentId: parentId ? Number(parentId) : null,
      targetDates: list,
      assignments: list.map((d) => ({ date: d, kind: 'day' })),
    };
    try {
      let r;
      if (selectedId) {
        r = await patchExtraAssignments(selectedId, payload);
      } else {
        r = await createExtraNode({ title: t, nodeType, ...payload });
      }
      if (!r?.ok) {
        const map = {
          extra_need_staff: '請選員工',
          extra_need_dates: '請選日期',
          extra_need_shifts: '請選班別',
          extra_need_assignments: '請指定班別或人員',
          not_extra: '此任務不是額外任務',
          parent_scope_mismatch: '分類與任務類型不符',
        };
        throw new Error(map[r?.error] || r?.message || '新增失敗');
      }
      reset();
      setOpen(false);
      bumpTreeTick?.();
      onCreated?.();
      await loadExtras();
    } catch (e) {
      setErr(e.message || '新增失敗');
    } finally {
      setSaving(false);
    }
  };

  const createCategory = async () => {
    const t = newCatTitle.trim();
    if (!t) { setErr('請填分類名稱'); return; }
    setSaving(true); setErr('');
    try {
      const r = await createExtraNode({
        title: t,
        nodeType: 'category',
        treeScope: 'extra',
      });
      if (!r?.ok) throw new Error(r?.error || '建立分類失敗');
      setNewCatTitle('');
      setShowNewCat(false);
      await loadExtras();
      if (r.id) setParentId(String(r.id));
    } catch (e) {
      setErr(e.message || '建立分類失敗');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="shift-extra-create-btn" onClick={() => setOpen(true)}>
        ＋ 新增額外任務
      </button>
    );
  }

  return (
    <div className="shift-card shift-extra-create-card">
      <div className="shift-fc-name">新增額外任務</div>
      {err ? <div className="shift-login-err" style={{ marginTop: 8 }}>{err}</div> : null}

      <div style={{ marginTop: 10 }}>
        <label className="shift-extra-label">分類</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            className="shift-login-inp"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            disabled={locked}
          >
            <option value="">未分類</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          {!locked && (
            <button type="button" className="shift-mb-btn" onClick={() => setShowNewCat((v) => !v)}>
              {showNewCat ? '取消' : '＋ 新分類'}
            </button>
          )}
        </div>
        {showNewCat && !locked ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              className="shift-login-inp"
              placeholder="分類名稱"
              value={newCatTitle}
              onChange={(e) => setNewCatTitle(e.target.value)}
            />
            <button type="button" className="shift-mb-btn" disabled={saving} onClick={createCategory}>建立</button>
          </div>
        ) : null}
      </div>

      <div style={{ marginTop: 10 }}>
        <label className="shift-extra-label">標題</label>
        {locked ? (
          <LockedBar>{title}</LockedBar>
        ) : (
          <input
            className="shift-login-inp"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="任務標題"
          />
        )}
      </div>

      {!locked && extras.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="shift-mb-btn" onClick={() => setRecentOpen((v) => !v)}>
            {recentOpen ? '收合既有' : '選既有任務'}
          </button>
          {recentOpen && (
            <div style={{ marginTop: 6 }}>
              {extras.slice(0, 12).map((x) => (
                <button
                  key={x.id}
                  type="button"
                  className="shift-mb-btn"
                  style={{ marginRight: 6, marginBottom: 6 }}
                  onClick={() => pickExisting(x.id)}
                >{x.title}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <label className="shift-extra-label">類型</label>
        {locked ? (
          <LockedBar>{nodeType === 'text' ? '文字' : '勾選'}</LockedBar>
        ) : (
          <select className="shift-login-inp" value={nodeType} onChange={(e) => setNodeType(e.target.value)}>
            <option value="check">勾選</option>
            <option value="text">文字</option>
          </select>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <label className="shift-extra-label">日期</label>
        <input
          type="date"
          className="shift-login-inp"
          value={date}
          onChange={(e) => {
            const v = e.target.value;
            setDate(v);
            if (v) setDates([v]);
          }}
        />
        {selected && (selected.assignments || []).length > 0 && (
          <button type="button" className="shift-mb-btn" style={{ marginTop: 6 }} onClick={() => setHistOpen((v) => !v)}>
            {histOpen ? '收合歷史日期' : '歷史日期'}
          </button>
        )}
        {histOpen && selected?.assignments?.length ? (
          <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text2)' }}>
            {(selected.assignments || []).map((a) => assignLine(a)).join('、')}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" className="shift-mb-btn" onClick={() => { reset(); setOpen(false); }}>取消</button>
        <button type="button" className="shift-mb-btn" disabled={saving} onClick={submit}>
          {saving ? '儲存中…' : (selectedId ? '追加日期' : '建立')}
        </button>
      </div>
    </div>
  );
}
